/**
 * @fileoverview Job Actions Hook
 * 
 * Handles API interactions for optimization jobs - starting, cancelling,
 * removing, and fetching jobs. Single responsibility: API calls.
 */

import { useCallback } from 'react';
import { enhancedBacktestService } from '../../services/optimization/EnhancedBacktestService';
import { jobsIntegrationService } from '../../services/jobs/JobsIntegrationService';
import { optimizationDebugLog } from '../../utils/optimizationDebugLogger';
import { sanitizeJobForMemory, RECENT_COMPLETED_WINDOW_MS, triggerGarbageCollection } from '../../utils/optimizationJobUtils';
import type { OptimizationJob, ApiTypes } from './types';

export interface JobFetchResult {
  /** All fetched jobs */
  allJobs: OptimizationJob[];
  /** Jobs to keep in memory (filtered for performance) */
  jobsToKeep: OptimizationJob[];
  /** Jobs that should be stored to database */
  jobsToStore: OptimizationJob[];
}

export interface UseJobActionsReturn {
  /** Fetch all jobs from the server */
  fetchJobs: () => Promise<JobFetchResult>;
  /** Start a new optimization job */
  startOptimization: (request: ApiTypes.OptimizationRequest) => Promise<{
    optimizationId: string;
    newJob: OptimizationJob;
  }>;
  /** Cancel a running optimization */
  cancelOptimization: (jobId: string) => Promise<void>;
  /** Remove a job from the backend */
  removeJob: (jobId: string) => Promise<void>;
  /** Store a completed job to the database */
  storeCompletedJob: (job: OptimizationJob) => Promise<void>;
  /** Get historical jobs from the database */
  getHistoricalJobs: (limit?: number) => Promise<OptimizationJob[]>;
}

/**
 * Hook for optimization job API actions
 */
export function useJobActions(): UseJobActionsReturn {
  const fetchJobs = useCallback(async (): Promise<JobFetchResult> => {
    const allJobs = await enhancedBacktestService.getOptimizationJobs();
    const now = Date.now();

    // Log status distribution
    const statusBuckets = allJobs.reduce<Record<string, string[]>>((acc, job) => {
      const bucket = acc[job.status] || [];
      bucket.push(job.id);
      acc[job.status] = bucket;
      return acc;
    }, {});

    optimizationDebugLog('useJobActions', 'fetchJobs received jobs', {
      total: allJobs.length,
      statusBuckets,
    }, { console: false });

    // Filter jobs to keep in memory for performance
    const activeAndQueued = allJobs.filter(
      (job) => job.status === 'running' || job.status === 'queued'
    );

    const recentlyCompleted = allJobs
      .filter((job) => job.status === 'completed')
      .filter((job) => {
        const referenceTime = job.completedAt
          ? new Date(job.completedAt)
          : new Date(job.startTime);
        return now - referenceTime.getTime() <= RECENT_COMPLETED_WINDOW_MS;
      })
      .sort((a, b) => {
        const timeA = a.completedAt ? new Date(a.completedAt).getTime() : new Date(a.startTime).getTime();
        const timeB = b.completedAt ? new Date(b.completedAt).getTime() : new Date(b.startTime).getTime();
        return timeB - timeA;
      })
      .slice(0, 5)
      .map(sanitizeJobForMemory);

    const recentFailedJobs = allJobs
      .filter((job) => job.status === 'failed')
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
      .slice(0, 1)
      .map(sanitizeJobForMemory);

    const jobsToKeep = [...activeAndQueued, ...recentlyCompleted, ...recentFailedJobs];
    const jobsToStore = allJobs.filter((job) => job.status === 'completed');

    return { allJobs, jobsToKeep, jobsToStore };
  }, []);

  const startOptimization = useCallback(async (
    request: ApiTypes.OptimizationRequest
  ): Promise<{ optimizationId: string; newJob: OptimizationJob }> => {
    const response = await enhancedBacktestService.startOptimization(request);

    if (!response.optimizationId) {
      throw new Error('Failed to start optimization');
    }

    // Register with Jobs system for unified tracking
    jobsIntegrationService.registerOptimizationJob({
      jobId: response.optimizationId,
      symbol: request.symbol,
      interval: request.interval,
      strategy: request.strategy,
      totalCombinations: request.totalCombinations || 0,
    });

    const newJob: OptimizationJob = {
      id: response.optimizationId,
      status: 'queued',
      strategy: request.strategy,
      symbol: request.symbol,
      interval: request.interval,
      progress: 0,
      current: 0,
      total: 1,
      startTime: new Date().toISOString(),
    };

    return { optimizationId: response.optimizationId, newJob };
  }, []);

  const cancelOptimization = useCallback(async (jobId: string): Promise<void> => {
    await enhancedBacktestService.cancelOptimization(jobId);
    
    // Trigger garbage collection if available
    triggerGarbageCollection();
  }, []);

  const removeJob = useCallback(async (jobId: string): Promise<void> => {
    await enhancedBacktestService.removeOptimizationJob(jobId);
  }, []);

  const storeCompletedJob = useCallback(async (job: OptimizationJob): Promise<void> => {
    try {
      await enhancedBacktestService.storeCompletedJob(job);
    } catch {
      // Don't block UI updates if storage fails
    }
  }, []);

  const getHistoricalJobs = useCallback(async (limit: number = 10): Promise<OptimizationJob[]> => {
    try {
      const { jobs } = await enhancedBacktestService.getHistoricalJobs({ page: 1, pageSize: limit });
      return jobs;
    } catch {
      return [];
    }
  }, []);

  return {
    fetchJobs,
    startOptimization,
    cancelOptimization,
    removeJob,
    storeCompletedJob,
    getHistoricalJobs,
  };
}
